import React, { useState } from 'react';
import toast from 'react-hot-toast';
import {
  BOOKING_KIND_LABELS,
  BOOKING_STAGE_LABELS,
  BOOKING_STATUS_LABELS,
  BookingRequestResponse,
  OrganizerPortalSubmissions,
  PRICE_VARIANT_LABELS,
  PROMOTION_TIER_SALES,
  formatEuro,
  formatIban,
  isZeroRated,
  payableTaxLabel,
  vatNote,
} from '@radevents/shared';
import bookingService from '../../../services/bookingService';
import { INK, MUTED, GREEN, AMBER } from '../organizerSpace';
import { Chip, EDIT_STATUS_LABELS, EDIT_STATUS_COLORS } from './chrome';

const copyText = (value: string, label: string) => {
  if (!navigator.clipboard) {
    toast.error(`${label} konnte nicht kopiert werden`);
    return;
  }
  navigator.clipboard.writeText(value)
    .then(() => toast.success(`${label} kopiert`))
    .catch(() => toast.error(`${label} konnte nicht kopiert werden`));
};

/**
 * Rechnung vorab anfordern.
 *
 * Steht hier an der offenen Bestellung und nicht im Checkout: im Kaufmoment weiß der Vorstand
 * meist noch nicht, dass seine Buchhaltung eine Rechnung braucht — er erfährt es, wenn er die
 * Zahlungsaufforderung weiterleitet und sie zurückbekommt. Ein Häkchen im Kauf übersähe genau
 * der, für den es da ist, und kostete alle anderen eine Entscheidung.
 *
 * Der Satz nennt die Folge: ab der Rechnung ist die Bestellung verbindlich. Wer heute nicht
 * überweist, lässt die Anfrage verfallen und es ist nichts passiert; nach einer Rechnung führt
 * der Weg zurück nur über eine Stornorechnung.
 */
const InvoiceRequest: React.FC<{
  organizerId: number;
  booking: BookingRequestResponse;
  onChanged: () => void;
}> = ({ organizerId, booking, onChanged }) => {
  const [busy, setBusy] = useState(false);

  if (booking.invoiceNumber) {
    return (
      <p style={{ margin: '4px 0 0', color: MUTED }}>
        Rechnung <strong>{booking.invoiceNumber}</strong> ist per E-Mail unterwegs — sie kommt von
        der GSK-IT UG, die Radsport-Events betreibt. Die Rechnungsnummer ist zugleich dein
        Verwendungszweck.
      </p>
    );
  }

  if (!booking.canRequestInvoice) return null;

  if (booking.invoiceRequestedAt) {
    return (
      <p style={{ margin: '4px 0 0', color: MUTED }}>
        Rechnung ist angefordert. Wir schicken sie dir per E-Mail zu — du musst nichts weiter tun.
      </p>
    );
  }

  const request = async () => {
    // Nachfragen, weil der Schritt die Bestellung verbindlich macht: danach gibt es kein
    // stilles Zurückziehen mehr, sondern nur noch eine Stornorechnung. Der Satz steht hier
    // und nicht nur als Titel am Knopf — gelesen wird, was den Klick aufhält.
    if (!window.confirm(
      'Wir schicken dir die Rechnung per E-Mail — sie kommt von der GSK-IT UG, die '
      + 'Radsport-Events betreibt.\n\nDamit ist deine Bestellung verbindlich. '
      + 'Freigeschaltet wird wie immer nach dem Zahlungseingang.'
    )) return;
    setBusy(true);
    try {
      await bookingService.requestInvoice(organizerId, booking.id);
      toast.success('Rechnung ist unterwegs');
      onChanged();
    } catch (error: any) {
      toast.error(error?.response?.data?.message ?? 'Die Rechnung ließ sich nicht anfordern');
      // Auch im Fehlerfall neu laden: der Wunsch ist vorgemerkt, und das soll er sehen.
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  // Der Knopf steht für sich, die Erklärung hängt als Titel daran: in der Zeile neben
  // „Zahlungsdetails ansehen" ist Platz für einen Knopf, nicht für einen Absatz. Wer wissen
  // will, was er auslöst, bekommt es im Bestätigungsdialog noch einmal gesagt.
  return (
    <button
      type="button"
      className="vb-btn"
      disabled={busy}
      onClick={request}
      title="Wir schicken dir die Rechnung vorab per E-Mail. Damit ist die Bestellung verbindlich; freigeschaltet wird wie immer nach dem Zahlungseingang."
    >
      {busy ? 'Einen Moment …' : 'Rechnung anfordern'}
    </button>
  );
};

/** Offene Zahlungen müssen nach dem Schließen des Buchungsdialogs wiederauffindbar bleiben. */
const PaymentDetails: React.FC<{
  organizerId: number;
  booking: BookingRequestResponse;
  onChanged: () => void;
}> = ({ organizerId, booking, onChanged }) => {
  const [expanded, setExpanded] = useState(false);
  const bank = booking.bank;
  if (booking.status !== 'OPEN') return null;
  const due = booking.paymentDueAt
    ? new Date(booking.paymentDueAt).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
    : null;
  return (
    <div style={{ marginTop: 14 }}>
      {/* Beide Knöpfe nebeneinander und **offen**. „Rechnung anfordern" lag zuerst im
          eingeklappten Zahlungsblock — dort findet es niemand: wer eine Rechnung braucht, sucht
          keine „Zahlungsdetails", er sucht eine Rechnung. Ein Knopf hinter einer Klappe ist für
          den, der ihn braucht, so gut wie nicht vorhanden. */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        <button
          type="button"
          className="vb-btn"
          aria-expanded={expanded}
          onClick={() => setExpanded(value => !value)}
        >
          {expanded ? 'Zahlungsdetails schließen' : 'Zahlungsdetails ansehen'}
        </button>
        <InvoiceRequest organizerId={organizerId} booking={booking} onChanged={onChanged} />
      </div>
      {expanded && (
        <div style={{ marginTop: 10, padding: '14px 16px', borderRadius: 12, background: 'rgba(146,44,87,.06)', border: '1px solid rgba(146,44,87,.16)' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: INK, marginBottom: 10 }}>
            Jetzt überweisen{due ? ` · bis ${due}` : ''}
          </div>
          <div style={{ display: 'grid', gap: 8, fontSize: 13.5, color: INK }}>
            {bank && (
              <div>
                <span style={{ color: MUTED }}>Empfänger</span><br />{bank.accountHolder}
                {bank.accountHolderAddress && <><br />{bank.accountHolderAddress}</>}
              </div>
            )}
            {bank && (
              <div style={{ display: 'flex', alignItems: 'end', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <span><span style={{ color: MUTED }}>IBAN</span><br /><strong>{formatIban(bank.iban)}</strong></span>
                <button type="button" className="vb-btn" onClick={() => copyText(bank.iban.replace(/\s/g, ''), 'IBAN')}>IBAN kopieren</button>
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'end', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <span><span style={{ color: MUTED }}>Verwendungszweck</span><br /><strong>{booking.paymentReference}</strong></span>
              <button type="button" className="vb-btn" onClick={() => copyText(booking.paymentReference, 'Verwendungszweck')}>Kopieren</button>
            </div>
            <div><span style={{ color: MUTED }}>Betrag</span><br /><strong>{formatEuro(booking.grossCents)}</strong></div>
            {!bank && (
              <p style={{ margin: '4px 0 0', color: MUTED }}>
                Die Bankverbindung steht in deiner Bestätigungs-E-Mail. Falls sie dort fehlt, schreib uns bitte kurz.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

interface Props {
  organizerId: number;
  /** Bestellungen — sie stehen hier, weil sie aus Sicht des Vereins Eingereichtes sind. */
  bookings: BookingRequestResponse[];
  /** Vorschläge zu Events und Stammdaten; `null` heißt „wird noch geladen“. */
  submissions: OrganizerPortalSubmissions | null;
  /** Nach dem Zurückziehen einer Anfrage: die Liste neu holen. */
  onBookingsChanged: () => void;
}

/**
 * Reiter *Eingereicht*: alles, was der Verein abgeschickt hat und worauf wir antworten.
 *
 * Ausgelagert aus `OrganizerManage` — der Inhalt ist unverändert.
 */
const SubmissionsTab: React.FC<Props> = ({ organizerId, bookings, submissions, onBookingsChanged }) => (
  <section>
    {/* Buchungsanfragen stehen hier und nicht im Reiter „Sichtbarkeit": aus Sicht des
        Veranstalters ist eine Bestellung dasselbe wie ein Vorschlag — etwas, das er
        abgeschickt hat und auf das wir antworten. Die Trennung „Angebot dort, Bestellung
        hier" ist unsere Einteilung, nicht seine; er sucht das Zurückziehen dort, wo alles
        Eingereichte steht. */}
    {bookings.length > 0 && (
      <p className="vb-eyebrow" style={{ marginBottom: 12 }}>Bestellungen</p>
    )}

    <div style={{ display: 'grid', gap: 12 }}>
      {bookings.map(booking => (
        <div key={booking.id} className="vb-card">
          <div className="vb-card-main">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
              <Chip
                /* Derselbe Stand wie in der Redaktionsansicht: „Rechnung verschickt" und
                   „Zahlung erhalten" sind genau die Auskünfte, nach denen ein Kassenwart hier
                   nachsieht. Bestandszeilen ohne `stage` fallen auf den alten Status zurück. */
                text={booking.stage
                  ? BOOKING_STAGE_LABELS[booking.stage]
                  : BOOKING_STATUS_LABELS[booking.status]}
                tone={booking.status === 'APPLIED' ? GREEN : booking.status === 'DECLINED' ? '#dc2626' : AMBER}
              />
              <h3 className="vb-title">{booking.priceLabel}</h3>
            </div>
            <p className="vb-meta">
              {BOOKING_KIND_LABELS[booking.kind]}
              {/* Die Stufe gehört zum Bestellten — sie steht hier, damit der Veranstalter
                  auch später noch sieht, was er gewählt hat. */}
              {booking.promoTier ? ` · ${PROMOTION_TIER_SALES[booking.promoTier].name}` : ''}
              {booking.variant ? ` · ${PRICE_VARIANT_LABELS[booking.variant]}` : ''}
              {booking.eventTitle ? ` · ${booking.eventTitle}` : ''}
            </p>
            {/* Bestellt wurde netto, überwiesen wird brutto (V159) — beides in einer Zeile,
                weil der Kassenwart hier beides sucht: den Posten, den er kennt, und den Betrag,
                den er überweist. */}
            <p className="vb-routes" style={{ letterSpacing: 0 }}>
              {/* Ohne MwSt. (Ausland, Reverse Charge) sind beide Zahlen gleich, dann nur eine. */}
              {booking.taxTreatment && isZeroRated(booking.taxTreatment)
                ? <>{formatEuro(booking.grossCents)} <span>zu zahlen, {payableTaxLabel(booking.taxTreatment, 0)}</span></>
                : <>
                  {formatEuro(booking.netCents)} <span>{vatNote(booking.vatPercent)}</span>
                  {' · '}{formatEuro(booking.grossCents)} <span>zu zahlen</span>
                </>}
            </p>
            <PaymentDetails organizerId={organizerId} booking={booking} onChanged={onBookingsChanged} />
            {booking.handledComment && (
              <p style={{ fontSize: 14, color: INK, margin: '10px 0 0' }}>
                Rückmeldung: {booking.handledComment}
              </p>
            )}
          </div>
          {/* Nach einer gestellten Rechnung fällt der Knopf weg statt ins Leere zu laufen: sie
              ist festgeschrieben und nur per Stornorechnung aufzuheben. Der Satz daneben sagt,
              wie es trotzdem geht — ein verschwundener Knopf ohne Erklärung ist eine Sackgasse. */}
          {booking.status === 'OPEN' && organizerId && (
            <div className="vb-actions">
              {booking.canCancel === false ? (
                <span style={{ fontSize: 13, color: MUTED }}>
                  Rechnung {booking.invoiceNumber} ist unterwegs — schreib uns kurz, wenn du die
                  Bestellung doch nicht brauchst.
                </span>
              ) : (
                <button
                  className="vb-btn"
                  onClick={() => bookingService.cancel(organizerId, booking.id)
                    .then(() => { toast.success('Bestellung zurückgezogen'); onBookingsChanged(); })
                    .catch((e: any) => toast.error(
                      e?.response?.data?.error ?? 'Konnte nicht zurückgezogen werden'))}
                >
                  Zurückziehen
                </button>
              )}
            </div>
          )}
        </div>
      ))}
    </div>

    {bookings.length > 0 && submissions
      && (submissions.eventEdits.length > 0 || submissions.organizerEdits.length > 0) && (
      <p className="vb-eyebrow" style={{ margin: '26px 0 12px' }}>Änderungen</p>
    )}
    {!submissions && <p style={{ color: MUTED, fontSize: 15 }}>Wird geladen…</p>}
    {submissions && submissions.eventEdits.length === 0
      && submissions.organizerEdits.length === 0 && bookings.length === 0 && (
      <div className="vb-empty">
        <p style={{ fontSize: 15, color: INK, margin: '0 0 4px' }}>Noch keine Bestellungen oder Änderungen.</p>
        <p style={{ fontSize: 14, color: MUTED, margin: 0 }}>
          Sobald du etwas änderst oder bestellst, steht hier, wie weit es ist.
        </p>
      </div>
    )}
    <div style={{ display: 'grid', gap: 12 }}>
      {submissions?.eventEdits.map(edit => (
        <div key={`e${edit.id}`} className="vb-card">
          <div className="vb-card-main">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
              <Chip text={EDIT_STATUS_LABELS[edit.status]} tone={EDIT_STATUS_COLORS[edit.status]} />
              <h3 className="vb-title">{edit.title}</h3>
            </div>
            <p className="vb-meta">
              {edit.type === 'CREATE' ? 'Neues Event' : 'Änderung'}
              {edit.createdAt ? ` · ${new Date(edit.createdAt).toLocaleDateString('de-DE')}` : ''}
            </p>
            {edit.reviewComment && (
              <p style={{ fontSize: 14, color: INK, margin: '10px 0 0' }}>
                Rückmeldung: {edit.reviewComment}
              </p>
            )}
          </div>
        </div>
      ))}
      {submissions?.organizerEdits.map(edit => (
        <div key={`o${edit.id}`} className="vb-card">
          <div className="vb-card-main">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
              <Chip text={EDIT_STATUS_LABELS[edit.status]} tone={EDIT_STATUS_COLORS[edit.status]} />
              <h3 className="vb-title">Stammdaten</h3>
            </div>
            <p className="vb-meta">
              {edit.createdAt ? new Date(edit.createdAt).toLocaleDateString('de-DE') : ''}
            </p>
            {edit.reviewerComment && (
              <p style={{ fontSize: 14, color: INK, margin: '10px 0 0' }}>
                Rückmeldung: {edit.reviewerComment}
              </p>
            )}
          </div>
        </div>
      ))}
    </div>
  </section>
);

export default SubmissionsTab;
